import { $el, UILink } from '../ui/elements';
import { UIClassesSpec } from '../ui/types';
import { UIView } from '../ui/view';
import { makeCSV } from '../y/csv';
import { yTextToBlob } from '../y/file-utils';
import { yClamp } from '../y/math';

export interface CHRow {
    labels: string[];
    values: (number | undefined)[];
    ref?: number | number[];
    total?: boolean;
}

export interface CHCol {
    title: string;
    width: number;
    classes?: UIClassesSpec;
}

export interface CHTableData {
    hue?: number;
    saturation?: number;
    absolute: boolean;
    percentage: boolean;
    rows: CHRow[];
    label_cols: CHCol[];
    cols: CHCol[];
}

function makeCell(hue: number, saturation: number, value: number, base: number, absolute: boolean, percentage: boolean) {
    const v = value / base;
    const text = absolute ? `${value}` : percentage ? `${(v * 100).toFixed(1)} %` : v.toFixed(2);
    const td = $el.create('td', undefined, text);
    td.style.backgroundColor = `hsl(${hue},${saturation}%,${yClamp(100 - 60 * v, 20, 100).toFixed(1)}%`;
    td.style.color = v > 0.5 ? 'white' : 'black';
    return [td, text] as const;
}

export class CHTable extends UIView {
    // private _data!: CHTableData;
    private _download_link!: UILink;

    constructor() {
        super('div', 'ch-table b');
    }

    set data(data: CHTableData) {
        // this._data = data;

        const hue = data.hue ?? 298;
        const saturation = data.saturation ?? 28;
        const absolute = data.absolute;
        const percentage = data.percentage;

        const csv_array: (string | number)[][] = [];

        const trh = $el.create('tr', 'ch-row');
        const csv_header: (string | number)[] = [];
        const trs: HTMLTableRowElement[] = [];
        let width = 0;

        for(const col of data.label_cols) {
            const th = $el.create('th', ['m-label', col.classes], col.title);
            th.style.width = col.width + 'px';
            th.style.left = width + 'px';
            trh.appendChild(th);
            width += col.width;
            csv_header.push(col.title);
        }

        for(const col of data.cols) {
            const th = $el.create('th', undefined, col.title);
            th.style.width = col.width + 'px';
            trh.appendChild(th);
            width += col.width;
            csv_header.push(col.title);
        }

        csv_array.push(csv_header);

        for(let i = 0; i < data.rows.length; i++) {
            const row = data.rows[i];
            const csv_row: (string | number)[] = [];
            const tr = $el.create('tr', ['ch-row', row.total && 'm-total', i == 0 && 'm-first']);

            let width = 0;

            for(let i = 0; i < data.label_cols.length; i++) {
                const col = data.label_cols[i];
                const td = $el.create('td', ['m-label', col.classes], row.labels[i]);
                csv_row.push(row.labels[i]);
                td.style.left = width + 'px';
                tr.ui_add(td);
                width += col.width;
            }

            for(let i = 0; i < data.cols.length; i++) {
                const ref = row.ref;
                const v = row.values[i];
                if(v == undefined) {
                    tr.ui_add($el.create('td'));
                    csv_row.push('');
                } else {
                    const [td, formatted] = makeCell(hue, saturation, v, (Array.isArray(ref) ? ref[i] : ref) || 1, absolute, percentage);
                    tr.ui_add(td);
                    csv_row.push(formatted);
                }
            }

            trs.push(tr);
            csv_array.push(csv_row);
        }

        const table = $el.create('table', undefined, [
            $el.create('thead', undefined, trh),
            $el.create('tbody', undefined, trs)
        ]);
        table.style.width = width + 'px';
        this.ui_empty(table);

        const csv = makeCSV(csv_array);
        const csv_url = URL.createObjectURL(yTextToBlob(csv, 'text/csv'));
        const csv_filename = 'retention.csv';

        this._download_link = { href: csv_url, download: csv_filename };
    }

    get download_link() {
        return this._download_link;
    }
}
